import {
  type BriefEdit,
  type BriefFramingMarkers,
  type ChatStepSlug,
  briefFramingMarkers,
} from '@sigora/domain';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import type { BriefReadView } from '../api/client.ts';
import {
  BRIEF_LOADING,
  BRIEF_MOVED_NOTICE,
  BRIEF_NOT_PUBLISHED,
  BRIEF_READ_FAILED,
  TRY_AGAIN_LABEL,
} from './brief-copy.ts';
import { BriefBlocks } from './brief/BriefBlocks.tsx';
import { BriefExport } from './brief/BriefExport.tsx';
import { BriefHistory } from './brief/BriefHistory.tsx';
import { BriefVersionView } from './brief/BriefVersionView.tsx';
import { revisionMarks } from './brief/RevisionMarks.tsx';
import { useBriefHistory } from './brief/use-brief-history.ts';
import {
  type BriefNews,
  type BriefReadCache,
  type BriefReadState,
  useBriefRead,
} from './brief/use-brief-read.ts';
import type { FollowUpScope } from './follow-ups/FollowUpThread.tsx';

/**
 * The published Brief a read holds: its current version, who wrote it and when, its content, its
 * history, Sigora's newest revision and the record its follow-ups left.
 */
export type PublishedBriefView = NonNullable<BriefReadView['brief']>;

/**
 * What either Brief step shows while its read is out, after it failed, or for a study with no
 * Brief to show — or the Brief itself, handed to `children` with the marks its framing carries.
 * A read again that failed behind a Brief on screen (`refreshFailed`) says so above it, with Try
 * again, and leaves the Brief drawn. That Try again waits while an edit, or the read after it, is
 * out (`busy`), so it never reads the Brief under an edit.
 */
export const BriefRead = ({
  state,
  reload,
  refreshFailed = false,
  busy = false,
  children,
}: {
  state: BriefReadState;
  reload: () => void;
  refreshFailed?: boolean;
  busy?: boolean;
  children: (brief: PublishedBriefView, markers: BriefFramingMarkers) => ReactNode;
}) => {
  if (state.status === 'loading') {
    return <p data-test-id="brief-loading">{BRIEF_LOADING}</p>;
  }

  if (state.status === 'failed') {
    return (
      <div data-test-id="brief-read-failed">
        <p className="chat-error" role="alert">
          {BRIEF_READ_FAILED}
        </p>
        <button type="button" data-test-id="brief-read-retry" onClick={reload}>
          {TRY_AGAIN_LABEL}
        </button>
      </div>
    );
  }

  const { brief } = state.read;
  if (brief === null) {
    return <p data-test-id="brief-not-published">{BRIEF_NOT_PUBLISHED}</p>;
  }

  return (
    <>
      {refreshFailed && (
        <div className="brief-refresh-failed" data-test-id="brief-refresh-failed">
          <p className="chat-error" role="alert">
            {BRIEF_READ_FAILED}
          </p>
          <button type="button" data-test-id="brief-refresh-retry" disabled={busy} onClick={reload}>
            {TRY_AGAIN_LABEL}
          </button>
        </div>
      )}
      {children(brief, briefFramingMarkers(brief.content))}
    </>
  );
};

const sameWording = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((words, index) => words === right[index]);

/**
 * The Brief: its version line and history, then one column of six blocks (`BriefBlocks`).
 *
 * Everything on it is what the stored version holds. Counts, Limitations and quotes are rendered
 * as stored and nothing is worked out here. The page hands a research question to the Brief's
 * conversation, through the composer's offer, and takes the researcher's framing — the Verdict's
 * wording, which summary bullets show, the recommendations' order — one edit at a time over the
 * version on screen (`useBriefRead`), each marked on its block. Sigora's newest revision tags the
 * blocks it changed and, while it is the current version, says its note and what it did to the
 * researcher's framing. An edit refused
 * because the Brief moved under it says so above the blocks while the Brief is read again. The notes
 * and the follow-up log the Brief's follow-ups left are drawn on it as it stands now, and each move
 * of `refreshToken` reads it again behind the page, so a follow-up finalized in the conversation, or
 * a version it wrote, lands here.
 *
 * Beside the version line, Download PDF saves a dated snapshot of the current version
 * (`BriefExport`). Another study's page reads its Brief from the loading line, which starts the
 * control afresh. A past version offers none: the control is hidden while one is open, so a PDF
 * already being made still lands.
 *
 * A past version opened from the history (`useBriefHistory`) replaces the blocks, read-only and with
 * no notes or log, until the researcher goes back to the current Brief. The Verdict's unsaved words
 * are kept here meanwhile, for this study only, so going back finds its editor open on them. Words
 * a Verdict save took are dropped once it lands, even when the save answered while a past version
 * was open, so going back finds the Verdict as saved.
 */
export const BriefStep = ({
  studyId,
  briefCache,
  offer,
  refreshToken = 0,
  briefNews = null,
}: {
  studyId: string;
  /** The study's Brief once read, kept by the shell across visits to the step. */
  briefCache: BriefReadCache;
  /**
   * Puts words in a step's composer when it is empty, and brings the composer to the reader either
   * way, with the card the next question is about; see `StudyShell`'s `askAbout`.
   */
  offer: (step: ChatStepSlug, text: string, scope?: FollowUpScope) => void;
  /** Moves when the study's conversation may have changed the Brief; each move reads it again. */
  refreshToken?: number;
  /** What the Brief's follow-ups say moved it: a read to draw, or word to read it again. */
  briefNews?: BriefNews | null;
}) => {
  const { state, reload, edit, saving, moved, noteMoved, refreshFailed } = useBriefRead(
    studyId,
    briefCache,
    refreshToken,
    briefNews,
  );
  const history = useBriefHistory(studyId, state.status === 'ready' ? state.read.brief : null);
  const [verdictDraft, setVerdictDraft] = useState<{ studyId: string; wording: string[] } | null>(
    null,
  );
  // The study the page is on, read when the words are kept. The keep itself never changes, so a
  // Verdict still drawn for the study before does not keep its words again under the next one.
  const draftStudy = useRef(studyId);
  useEffect(() => {
    draftStudy.current = studyId;
  }, [studyId]);
  const keepVerdictDraft = useCallback(
    (wording: string[] | null) =>
      setVerdictDraft(wording === null ? null : { studyId: draftStudy.current, wording }),
    [],
  );
  // A saved Verdict drops the words kept for it when they are the words it saved, for the study
  // that sent it; words typed since, any other edit, or a Verdict save that did not land keep them.
  const send = useCallback(
    async (change: BriefEdit) => {
      const sentFor = draftStudy.current;
      const outcome = await edit(change);
      if (change.op === 'reword_verdict' && outcome.status === 'saved') {
        const saved = change.wording;
        setVerdictDraft((draft) =>
          draft?.studyId === sentFor && sameWording(draft.wording, saved) ? null : draft,
        );
      }
      return outcome;
    },
    [edit],
  );
  const edits = {
    send,
    busy: saving,
    noteMoved,
    verdictDraft: verdictDraft?.studyId === studyId ? verdictDraft.wording : null,
    keepVerdictDraft,
  };
  // The card rides with the question and is said over the composer, so no words are put in it.
  const onAsk = (card: FollowUpScope) => offer('brief', '', card);

  return (
    <div className="brief" data-test-id="brief">
      {moved && (
        <p className="brief-notice" data-test-id="brief-moved-notice" role="status">
          {BRIEF_MOVED_NOTICE}
        </p>
      )}
      <BriefRead state={state} reload={reload} refreshFailed={refreshFailed} busy={saving}>
        {(brief, markers) => (
          <>
            <div className="brief-head">
              <BriefHistory
                version={brief.version}
                authoredBy={brief.authoredBy}
                history={brief.history}
                open={history.open}
                onToggle={history.toggle}
                viewing={history.viewing}
                onView={history.view}
                restoreFocusTo={history.restoreFocusTo}
                onFocusRestored={history.focusRestored}
              />
              <BriefExport studyId={studyId} hidden={history.viewing !== null} />
            </div>
            {history.viewing === null ? (
              <BriefBlocks
                content={brief.content}
                markers={markers}
                edits={edits}
                marks={revisionMarks(brief.lastRevision, brief.version)}
                record={brief.record}
                onAsk={onAsk}
              />
            ) : (
              <BriefVersionView
                studyId={studyId}
                version={history.viewing}
                history={brief.history}
                cache={briefCache}
                onBack={history.back}
                onAsk={onAsk}
              />
            )}
          </>
        )}
      </BriefRead>
    </div>
  );
};
